import React, { useEffect, useState, useMemo, createContext, useContext } from 'react';
import axios from 'axios';
import { Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  Popover, PopoverTrigger, PopoverContent,
} from './ui/popover';

import { apiDetail } from '../lib/apiError';
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 2F — Metric source / caveat tooltip.
 *
 * Hydrates ``METRIC_SOURCE_REGISTRY`` from the backend once per
 * session and exposes a lightweight ``<MetricInfoTooltip metricKey=…>``
 * component. Every operator-facing headline number that is fed by a
 * registered metric should render this next to its label.
 *
 * The frontend never authors caveat copy — it is the backend registry
 * or nothing. If a key is missing from the registry, the tooltip
 * silently renders nothing (fallback: no info icon).
 */
const MetricRegistryContext = createContext({ metrics: {}, loading: false, error: null });

export function MetricRegistryProvider({ children }) {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/accounting/metric-definitions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      if (cancelled) return;
      setMetrics(res.data?.metrics || {});
      setError(null);
    }).catch((e) => {
      if (cancelled) return;
      // Silent fallback — tooltips just won't render. Never break the page.
      setError(apiDetail(e) || 'metric registry unavailable');
      setMetrics({});
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token]);

  const value = useMemo(() => ({ metrics, loading, error }), [metrics, loading, error]);
  return (
    <MetricRegistryContext.Provider value={value}>
      {children}
    </MetricRegistryContext.Provider>
  );
}

export function useMetricRegistry() {
  return useContext(MetricRegistryContext);
}

export function MetricInfoTooltip({ metricKey, className = '' }) {
  const { metrics } = useMetricRegistry();
  const meta = metrics[metricKey];
  if (!meta) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${meta.label}`}
          className={`inline-flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 ${className}`}
          data-testid={`metric-info-${metricKey}`}
        >
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="w-80 text-sm" data-testid={`metric-tooltip-${metricKey}`}>
        <div className="font-semibold text-slate-900 dark:text-white text-sm mb-1">{meta.label}</div>
        {meta.source_type_copy && (
          <div className="text-xs text-slate-600 dark:text-slate-300 mb-1">
            {meta.source_type_copy}
          </div>
        )}
        {meta.gl_backed_copy && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
            {meta.gl_backed_copy}
          </div>
        )}
        {meta.caveat && (
          <div className="text-[11px] text-amber-700 dark:text-amber-300 border-t border-slate-100 dark:border-slate-700 pt-1 mt-1">
            <span className="font-medium">Caveat:</span> {meta.caveat}
          </div>
        )}
        {meta.tables && meta.tables.length > 0 && (
          <div className="text-[10px] text-slate-400 mt-2 italic">
            Data source: {meta.tables.join(', ')}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
